import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Building2, CheckCircle2, Clock, Plus, ShieldAlert, MessageSquare, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner, EmptyState, LicenceBadge } from "@/components/shared";
import { MessagesPanel } from "@/components/MessagesPanel";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_COLOR = {
  open: "bg-[#E8F5F1] text-[#007F83]", draft: "bg-gray-100 text-gray-600",
  awarded: "bg-blue-50 text-blue-700", completed: "bg-green-50 text-green-700",
  approved: "bg-[#E8F5F1] text-[#007F83]", submitted: "bg-amber-50 text-amber-700",
  draft_company: "bg-gray-100 text-gray-600",
};

export default function DashboardPage() {
  const { user, verifyEmail } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [companies, setCompanies] = useState(null);

  useEffect(() => {
    api.get("/my/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
    api.get("/my/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  return (
    <div className="propora-container py-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Hi, {user?.name?.split(" ")[0]}</h1>
          <p className="text-[#4A5568] mt-1">Manage your jobs, quotes and business profile.</p>
        </div>
        <Link to="/post-a-job"><Button className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Plus size={18} className="mr-1" /> Post a job</Button></Link>
      </div>

      {!user?.email_verified && (
        <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="dashboard-verify-banner">
          <span className="text-sm text-amber-800 inline-flex items-center gap-2"><ShieldAlert size={18} /> Your email isn't verified yet. Verify to publish jobs. (Dev mode — instant verification available.)</span>
          <Button size="sm" onClick={async () => { await verifyEmail(); toast.success("Email verified"); }} data-testid="dashboard-verify-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Verify now</Button>
        </div>
      )}

      <Tabs defaultValue="jobs" className="mt-8">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase size={15} className="mr-1.5" /> My jobs</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare size={15} className="mr-1.5" /> Messages</TabsTrigger>
          <TabsTrigger value="business" data-testid="tab-business"><Building2 size={15} className="mr-1.5" /> My business</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-6">
          {!jobs ? <Spinner /> : jobs.length === 0 ? (
            <EmptyState title="No jobs yet" subtitle="Post your first job and get responses from listed companies.">
              <Link to="/post-a-job"><Button className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Post a job</Button></Link>
            </EmptyState>
          ) : (
            <div className="space-y-4">
              {jobs.map((j) => (
                <Link to={`/dashboard/jobs/${j.id}`} key={j.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5 flex items-center justify-between gap-4 card-lift" data-testid={`dash-job-${j.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-lg text-[#0B1733]">{j.title}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[j.status] || "bg-gray-100"}`}>{j.status}</span>
                    </div>
                    <p className="text-sm text-[#4A5568] mt-1">{j.service_name} · {j.area}</p>
                  </div>
                  <div className="text-right text-sm text-[#4A5568] flex items-center gap-3">
                    <div><p className="font-semibold text-[#0B1733]">{j.response_count}/{j.max_responses}</p><p className="text-xs">responses</p></div>
                    <ArrowRight size={18} className="text-[#007F83]" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <MessagesPanel />
        </TabsContent>

        <TabsContent value="business" className="mt-6">
          {!companies ? <Spinner /> : companies.length === 0 ? (
            <EmptyState title="List your company" subtitle="Register your business, upload your trade licence and start responding to jobs after approval.">
              <Link to="/business/register"><Button className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Register a business</Button></Link>
            </EmptyState>
          ) : (
            <div className="space-y-4">
              {companies.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`dash-company-${c.slug}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-lg text-[#0B1733]">{c.trading_name}</h3>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[c.status] || "bg-gray-100 text-gray-600"}`}>{c.status.replace("_", " ")}</span>
                      </div>
                      <p className="text-sm text-[#4A5568] mt-1">{c.services?.join(" · ") || c.service_slugs?.join(" · ")}</p>
                    </div>
                    {c.licence_checked && <LicenceBadge date={c.licence_check_date} />}
                  </div>
                  {c.status === "draft" && (
                    <p className="mt-3 text-sm text-amber-700 inline-flex items-center gap-1.5"><Clock size={14} /> Draft — complete onboarding and submit for review.</p>
                  )}
                  {(c.status === "submitted" || c.status === "under_review") && (
                    <p className="mt-3 text-sm text-amber-700 inline-flex items-center gap-1.5"><Clock size={14} /> Pending admin licence review. You can preview your profile privately.</p>
                  )}
                  {c.status === "approved" && (
                    <div className="mt-3 flex items-center gap-4">
                      <Link to={`/business/${c.id}`} className="inline-flex items-center gap-1.5 text-sm text-[#007F83] font-medium hover:underline" data-testid={`workspace-${c.slug}`}><Briefcase size={14} /> Open workspace (jobs, quotes, promotions)</Link>
                      <Link to={`/companies/${c.slug}`} className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83]"><CheckCircle2 size={14} /> Public profile</Link>
                    </div>
                  )}
                </div>
              ))}
              <Link to="/business/register"><Button variant="outline" className="rounded-full"><Plus size={16} className="mr-1" /> Add another business</Button></Link>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
